// The routes in a status row's 'options' prose, read as structure, and the network
// application each Grid route most likely rests on.
import type { Application } from "../api/hooks/useAgentPortfolio";

// A Grid route's stages while it is open, in order of progress (APPROVED, retired, still
// on stored rows), and how a route or an application closed. Must agree with GRID_ROW's
// application_stages in inrange_agents/portfolio_row.py, which a test there compares.
export const OPEN_ROUTE_STAGES: readonly string[] = [
  "APPLIED",
  "OFFER RECEIVED",
  "TENTATIVELY ACCEPTED",
  "ACCEPTED",
  "APPROVED",
];
export const CLOSED_STAGES: readonly string[] = [
  "SUPERSEDED",
  "LAPSED",
  "REJECTED",
  "WITHDRAWN",
  "BUDGET ESTIMATE",
];

// A labelled line: "WHERE IT STANDS:", "OPTION 1 (ACCEPTED):". A route in 'options'
// may carry its stage in brackets, shown as a chip. Must agree with _OPTION_MARK_RE in
// inrange_agents/portfolio_row.py, which refuses a stage the route cannot carry.
export const LEAD_LABEL =
  /^([A-Z][A-Z0-9'/ -]{2,40}?)(?: \(([A-Z][A-Z ]{2,30})\))?:[ \t]*/;

// A point written as "- ...".
export const BULLET = /^[-*•]\s+/;

// A route's detail: "- Capacity: ..." or "- **Capacity:** ...".
export const POINT_LABEL =
  /^(?:\*\*)?([A-Za-z][A-Za-z0-9'/ -]{1,40}?)(?::\*\*|\*\*:|:)(?:[ \t]+|$)/;

// A route's own line, also in the shapes rows stored before the prompt asked for plain
// capitals still carry ("- **Option 1:** ...", "Option 2 (APPROVED): ...").
const ROUTE_LINE =
  /^(?:[-*•]\s+)?(?:\*\*)?(OPTION|RULED OUT) (\d+)(?: \(([A-Z][A-Z ]{2,30})\))?(?::\*\*|\*\*:|:)[ \t]*/i;

export interface RoutePoint {
  label: string;
  text: string;
}

export interface OptionRoute {
  number: number;
  ruledOut: boolean;
  // The bracketed stage; "" where the route carries none.
  stage: string;
  // What follows the label on its own line ("the 990 kW installed application").
  title: string;
  points: RoutePoint[];
}

export const splitPoint = (text: string): RoutePoint => {
  const label = POINT_LABEL.exec(text);
  return label
    ? { label: label[1].trim(), text: text.slice(label[0].length) }
    : { label: "", text };
};

// Every OPTION / RULED OUT route in order. Anything else under a route that is not
// another labelled line (prose the agent wrapped onto a new line) is kept as an
// unlabelled point; a labelled line such as THE CHOICE ends the route.
export const parseOptionRoutes = (options: string): OptionRoute[] => {
  const routes: OptionRoute[] = [];
  let current: OptionRoute | null = null;
  for (const raw of options.split("\n")) {
    const line = raw.trim();
    if (!line) continue;
    const route = ROUTE_LINE.exec(line);
    if (route) {
      const ruledOut = route[1].toUpperCase() === "RULED OUT";
      // ROUTE_LINE reads shapes the backend does not check, so a ruled-out route keeps
      // only a closed stage: "(declined by ESP)" is prose, not a chip.
      const stage = (route[3] ?? "").toUpperCase();
      current = {
        number: Number(route[2]),
        ruledOut,
        stage: ruledOut && !CLOSED_STAGES.includes(stage) ? "" : stage,
        title: line.slice(route[0].length),
        points: [],
      };
      routes.push(current);
      continue;
    }
    const bullet = BULLET.exec(line);
    if (bullet) {
      current?.points.push(splitPoint(line.slice(bullet[0].length)));
      continue;
    }
    if (LEAD_LABEL.test(line)) {
      current = null;
      continue;
    }
    current?.points.push({ label: "", text: line });
  }
  return routes;
};

// Every capacity a piece of text names, in kW ("990 kWp", "1.5 MW", "499kVA", "419 kWac").
const CAPACITY = /(\d[\d,]*(?:\.\d+)?)\s*([kM])(?:W|VA)(?:p|ac|dc|e)?\b/gi;

const capacitiesIn = (text: string): number[] =>
  [...text.matchAll(CAPACITY)].map((m) => {
    const value = Number(m[1].replace(/,/g, ""));
    return m[2].toUpperCase() === "M" ? value * 1000 : value;
  });

const sameSize = (a: number, b: number): boolean =>
  Math.abs(a - b) <= Math.max(1, 0.01 * Math.max(a, b));

// An application's stages a route at each standing can rest on. A live route with no
// stage is matched as APPLIED. A ruled-out one rests on a closed application, and a
// staged one also on WITHDRAWN, which some applications hold for a lapse or a decline.
const stagesFor = (route: OptionRoute): readonly string[] => {
  if (!route.ruledOut) return [route.stage || "APPLIED"];
  return route.stage ? [route.stage, "WITHDRAWN"] : CLOSED_STAGES;
};

// Most advanced first: live routes by how far the counterparty has moved on them
// (unstaged last), then ruled-out ones; within each, the higher number, as routes are
// numbered in the order they arose.
export const routeOrder = (a: OptionRoute, b: OptionRoute): number => {
  const rank = (r: OptionRoute): number =>
    r.ruledOut ? -2 : OPEN_ROUTE_STAGES.indexOf(r.stage);
  return rank(b) - rank(a) || b.number - a.number;
};

export type RouteApplication =
  | { kind: "matched"; application: Application }
  | { kind: "ambiguous"; candidates: number }
  | { kind: "none" };

// Each route's application: those at the route's stage, then the one whose kW figures
// the route's text names. A lone candidate is taken unless both name sizes and none
// agree (a SUPERSEDED original is not the declined reapplication beside it). An
// application is given to one route at most, and a route with no unique match says so
// rather than guessing.
export const matchRouteApplications = (
  routes: OptionRoute[],
  applications: Application[],
): RouteApplication[] => {
  const taken = new Set<Application>();
  return routes.map((route) => {
    const stages = stagesFor(route);
    const candidates = applications.filter(
      (app) => !taken.has(app) && stages.includes(app.stage),
    );
    if (candidates.length === 0) return { kind: "none" };
    const named = capacitiesIn(
      [route.title, ...route.points.map((p) => p.text)].join("\n"),
    );
    const sizes = (app: Application): number[] =>
      [app.pv_installed_kw, app.export_kw, app.battery_kw].filter(
        (kw): kw is number => kw !== null,
      );
    const score = (app: Application): number =>
      sizes(app).filter((kw) => named.some((n) => sameSize(n, kw))).length;
    let pick: Application | undefined;
    if (candidates.length === 1) {
      const only = candidates[0];
      const disagrees =
        named.length > 0 && sizes(only).length > 0 && score(only) === 0;
      if (disagrees) return { kind: "none" };
      pick = only;
    } else {
      const best = Math.max(...candidates.map(score));
      const top = candidates.filter((app) => score(app) === best);
      if (best > 0 && top.length === 1) pick = top[0];
    }
    if (!pick) return { kind: "ambiguous", candidates: candidates.length };
    taken.add(pick);
    return { kind: "matched", application: pick };
  });
};
